import { useMemo } from "react";
import type { Node, Edge } from "reactflow";
import { Position } from "reactflow";
import type { RoadmapArea, RoadmapNode } from "../../../data/ch_concepts";
import {
  AREA_NODE_WIDTH, AREA_NODE_HEIGHT,
  getCategoryColor,
} from "./constants";
import type { TopicsGroupNodeData } from "./TopicsGroupNode";

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface AreaGroupNodeData {
  type: "areaGroup";
  area: RoadmapArea;
  isExpanded: boolean;
  onToggle: (areaId: string) => void;
}

export interface RoadmapEdgeData {
  targetStatus: string;
  revealDelay: number;
}

// ─── Layout constants ──────────────────────────────────────────────────────────

const AREA_GAP_Y = 36;
const TOPICS_GROUP_OFFSET_X = 48;
const TOPICS_GROUP_WIDTH = 280;
const TOPIC_ROW_HEIGHT = 56;  // py-3 (24px) + text (~18px) + divider + wrapping buffer
const TOPICS_GROUP_PADDING = 4; // border + rounded container padding

// ─── Layout hook ───────────────────────────────────────────────────────────────

interface UseRoadmapLayoutParams {
  areas: RoadmapArea[];
  expandedAreaIds: Set<string>;
  onToggleArea: (areaId: string) => void;
  onTopicClick: (node: RoadmapNode, areaLabel: string, areaDescription: string) => void;
}

export function useRoadmapLayout({
  areas,
  expandedAreaIds,
  onToggleArea,
  onTopicClick,
}: UseRoadmapLayoutParams): { nodes: Node[]; edges: Edge[] } {
  return useMemo(() => {
    if (!areas.length) return { nodes: [], edges: [] };

    const nodes: Node[] = [];
    const edges: Edge[] = [];

    const areaX = 0;
    let currentY = 0;

    for (let i = 0; i < areas.length; i++) {
      const area = areas[i];
      const isExpanded = expandedAreaIds.has(area.id);
      const areaNodeId = `area::${area.id}`;

      // Area node
      nodes.push({
        id: areaNodeId,
        type: "areaGroup",
        position: { x: areaX, y: currentY },
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
        data: {
          type: "areaGroup",
          area,
          isExpanded,
          onToggle: onToggleArea,
        } satisfies AreaGroupNodeData,
      });

      // Chain edge: previous area → this area
      if (i > 0) {
        const prevAreaId = `area::${areas[i - 1].id}`;
        edges.push({
          id: `chain::${prevAreaId}->${areaNodeId}`,
          source: prevAreaId,
          target: areaNodeId,
          type: "roadmapEdge",
          sourceHandle: "bottom",
          targetHandle: undefined,
          data: { targetStatus: area.status, revealDelay: 0 } satisfies RoadmapEdgeData,
        });
      }

      // Topics group to the right
      let areaBlockHeight = AREA_NODE_HEIGHT;

      if (isExpanded && area.topics.length > 0) {
        const catColor = getCategoryColor(area.topics[0]?.category ?? "");
        const groupNodeId = `topics-group::${area.id}`;
        const groupX = areaX + AREA_NODE_WIDTH + TOPICS_GROUP_OFFSET_X;
        const groupY = currentY;

        const estimatedGroupHeight = area.topics.length * TOPIC_ROW_HEIGHT + TOPICS_GROUP_PADDING;

        nodes.push({
          id: groupNodeId,
          type: "topicsGroup",
          position: { x: groupX, y: groupY },
          sourcePosition: Position.Bottom,
          targetPosition: Position.Left,
          style: { width: TOPICS_GROUP_WIDTH },
          data: {
            type: "topicsGroup",
            topics: area.topics,
            areaLabel: area.label,
            areaDescription: area.description,
            categoryAccent: catColor.accent,
            onTopicClick,
          } satisfies TopicsGroupNodeData,
        });

        // Edge: area → topics group (right → left)
        edges.push({
          id: `${areaNodeId}->${groupNodeId}`,
          source: areaNodeId,
          target: groupNodeId,
          type: "roadmapEdge",
          sourceHandle: "right",
          targetHandle: "left",
          data: { targetStatus: area.status, revealDelay: 0 } satisfies RoadmapEdgeData,
        });

        areaBlockHeight = Math.max(AREA_NODE_HEIGHT, estimatedGroupHeight);
      }

      currentY += areaBlockHeight + AREA_GAP_Y;
    }

    return { nodes, edges };
  }, [areas, expandedAreaIds, onToggleArea, onTopicClick]);
}
